/* eslint-disable react-hooks/exhaustive-deps */
import { useRoutes } from 'react-router-dom';
import routes from './router';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ThemeProvider from './theme/ThemeProvider';
import { CssBaseline } from '@mui/material';
import { useAppDispatch, useAppSelector } from './app/hooks';
import Loading from './content/pages/Status/Loading';
import { LOG_PROJECT_ACTIVE } from './services/graphql/users/mutation';
import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { reducerSignIn } from './features/users';
import { useLocation } from 'react-router';
import LoadingProcess from './content/pages/Status/LoadingProcess';
import getDashboardDateTime from './utils/getDashboardDateTime';
import type { IProject } from './models/users';
import NewsLetterPreview from './content/main/CustomReport/Newsletter/NewsLetterPreview';
import AlertPreview from './content/settings/AlertSystem/components/AlertPreview';
import WhatsappAlertPreview from './content/settings/AlertSystem/components/WhatsappAlertPreview';
// import useBillingSystem from './services/useBillingSystem';

const getTimeValue = (date?: Date | string | number | null) => {
  if (!date) return null;
  const time = date instanceof Date ? date.getTime() : new Date(date).getTime();
  return Number.isNaN(time) ? null : time;
};

export const isNoDuplicateVersion = (
  project: Pick<IProject, 'createdByV6' | 'cutOffV6Socmed'> | undefined,
  start: Date | string
): boolean => {
  const startDate = getDashboardDateTime(start);
  const isNoDuplicateProject = project?.createdByV6;
  const cutOffTime = getTimeValue(project?.cutOffV6Socmed);
  // will use no duplicate endpoint if createdByV6 is true
  // and start date is later than cut off time
  return Boolean(
    isNoDuplicateProject &&
      startDate !== null &&
      cutOffTime !== null &&
      startDate >= cutOffTime
  );
};

const App = () => {
  const isAuthenticated = useAppSelector(
    (state) => state.users.isAuthenticated
  );
  const project = useAppSelector((state) => state.users.project);
  const timeframe = useAppSelector(
    (state) => state.analyticArguments.dateRange
  );

  const isOnboarding = useAppSelector(
    (state) => state.users.project?.onBoarding?.isOnBoarding
  );
  const isNetworkAnalysis = useAppSelector(
    (state) => state.users.project?.flags?.isSnaDna
  );
  const isCustomReport = useAppSelector(
    (store) => store.users.project?.flags?.isCustomReport
  );
  const isClustering = useAppSelector(
    (store) => store.users.project?.flags?.isClustering
  );
  const isOverview = useAppSelector(
    (store) => store.users.project?.flags?.isMainOverview
  );
  const isPeoplePanel = useAppSelector(
    (store) => store.users.project?.flags?.isPeoplePanel
  );
  const isEcommerce = useAppSelector(
    (store) => store.users.project?.flags?.isEcommerce
  );
  const customerType = useAppSelector(
    (store) => store.users.project?.customerType
  );

  const content = useRoutes(
    routes(
      isAuthenticated,
      isOnboarding,
      Boolean(isNetworkAnalysis),
      Boolean(isCustomReport),
      Boolean(isClustering),
      Boolean(isOverview),
      Boolean(isPeoplePanel),
      Boolean(isEcommerce),
      customerType === 'b2c',
      isNoDuplicateVersion(project, timeframe.start)
    )
  );

  const user = useAppSelector((state) => state.users);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const pathname = location.pathname.split('/').at(1) ?? '';

  const isLoadingProcess = pathname === 'loading-process';
  const isNewsletterPreview = pathname === 'news_letter';
  const isAlertPreview = pathname === 'alert';
  const isWhatsappAlertPreview = pathname === 'whatsapp-report';

  const isByPassLoading =
    isLoadingProcess ||
    isNewsletterPreview ||
    isAlertPreview ||
    isWhatsappAlertPreview;

  const ByPassLoadingComponent = () => {
    if (!isByPassLoading) return null;
    if (isLoadingProcess) return <LoadingProcess />;
    if (isNewsletterPreview) return <NewsLetterPreview />;
    if (isAlertPreview) return <AlertPreview />;
    if (isWhatsappAlertPreview) return <WhatsappAlertPreview />;
  };

  const [logProjectActive] = useMutation(LOG_PROJECT_ACTIVE, {
    onCompleted: ({ session }) => {
      if (!session?.token) return;
      const isAuth = session.token ? true : false;
      window.localStorage.setItem('token', session.token);
      dispatch(reducerSignIn(isAuth));
    }
  });

  useEffect(() => {
    if (user?.project?.id && user?.userProfile?.data?.userId) {
      window.localStorage.setItem('email', user.userProfile.data.email);
      logProjectActive({
        variables: {
          projectId: user?.project?.id,
          userId: user?.userProfile?.data?.userId
        }
      });
    }
  }, [user?.project?.id, user?.userProfile?.data?.userId]);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        {isByPassLoading ? (
          <ByPassLoadingComponent />
        ) : isAuthenticated && !project ? (
          <Loading />
        ) : (
          content
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
};
export default App;
